// src/components/FuelCardsTab.tsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Badge } from "@/components/ui/badge";
import { Eye, EyeOff, Pencil, RefreshCw, Plus, Ban, Search, X } from "lucide-react";

type FuelCardStatus = "active" | "inactive";
type FuelCardType = "Petrol" | "Diesel";

type FuelCardRow = {
  id: string;
  pool_number: string;
  card_type: FuelCardType | string;
  pin: string | null;
  status: FuelCardStatus | string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  vehicle_fuel_cards?: Array<{
    vehicle_number: string;
    unassigned_at: string | null;
  }> | null;
};

function maskPin(pin: string | null) {
  if (!pin) return "-";
  return "••••";
}

function statusBadgeVariant(status: string) {
  if (status === "active") return "default";
  return "secondary";
}

// Natural sort function - extracts numbers and sorts numerically
function naturalSort(a: string, b: string): number {
  const extractNumber = (str: string): number => {
    const match = str.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  };
  
  const numA = extractNumber(a);
  const numB = extractNumber(b);
  
  if (numA !== numB) {
    return numA - numB;
  }
  
  // If numbers are equal, fall back to string comparison
  return a.localeCompare(b);
}

// A restricted user (see src/lib/permissions.ts, Settings -> Team Access)
// sees only the card(s) assigned to their linked vehicle(s), and can never
// add/edit/deactivate a card -- fuel cards are a shared fleet resource, not
// something scoped to one vehicle the way editing a driver's own record is.
const FuelCardsTab: React.FC<{ isAdmin?: boolean; scopedVehicleNumbers?: string[] | null }> = ({ isAdmin = true, scopedVehicleNumbers = null }) => {
  const canManage = isAdmin && !scopedVehicleNumbers;
  const [cards, setCards] = useState<FuelCardRow[]>([]);
  const [loading, setLoading] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [assignmentFilter, setAssignmentFilter] = useState<"all" | "assigned" | "unassigned">("all");

  // Add new card form
  const [addOpen, setAddOpen] = useState(false);
  const [newCard, setNewCard] = useState<{
    pool_number: string;
    card_type: FuelCardType;
    pin: string;
    status: FuelCardStatus;
    notes: string;
  }>({
    pool_number: "",
    card_type: "Petrol",
    pin: "",
    status: "active",
    notes: "",
  });

  // Reveal PIN state
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<FuelCardRow | null>(null);
  const [editValues, setEditValues] = useState<{
    pool_number: string;
    card_type: FuelCardType;
    pin: string;
    status: FuelCardStatus;
    notes: string;
  }>({
    pool_number: "",
    card_type: "Petrol",
    pin: "",
    status: "active",
    notes: "",
  });

  const fetchCards = async () => {
    setLoading(true);
    try {
      // Pull cards + current assignment (active only)
      // Relationship relies on vehicle_fuel_cards.fuel_card_id -> fuel_cards.id FK
      const { data, error } = await supabase
        .from("fuel_cards")
        .select(
          `
          id,
          pool_number,
          card_type,
          pin,
          status,
          notes,
          created_at,
          updated_at,
          vehicle_fuel_cards (
            vehicle_number,
            unassigned_at
          )
        `
        )
        .order("pool_number", { ascending: true });

      if (error) throw error;

      // Keep only active assignment rows in memory (unassigned_at null)
      const cleaned = (Array.isArray(data) ? data : []).map((c: any) => {
        const vfc = Array.isArray(c.vehicle_fuel_cards) ? c.vehicle_fuel_cards : [];
        const active = vfc.filter((x: any) => x?.unassigned_at == null);
        return { ...c, vehicle_fuel_cards: active };
      });

      setCards(cleaned as FuelCardRow[]);
    } catch (e: any) {
      toast({
        title: "Error fetching fuel cards",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCards();
  }, []);

  const cardsWithAssignedVehicle = useMemo(() => {
    const mapped = (Array.isArray(cards) ? cards : []).map((c) => {
      const assignedVehicle =
        Array.isArray(c.vehicle_fuel_cards) && c.vehicle_fuel_cards.length > 0
          ? c.vehicle_fuel_cards[0]?.vehicle_number
          : null;

      return { ...c, assignedVehicle };
    });
    if (!scopedVehicleNumbers) return mapped;
    const scoped = new Set(scopedVehicleNumbers);
    return mapped.filter((c) => c.assignedVehicle && scoped.has(c.assignedVehicle));
  }, [cards, scopedVehicleNumbers]);

  // Filtered and sorted cards
  const filteredAndSortedCards = useMemo(() => {
    let result = [...cardsWithAssignedVehicle];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((card) => {
        return (
          card.pool_number.toLowerCase().includes(query) ||
          (card.card_type && card.card_type.toLowerCase().includes(query)) ||
          (card.notes && card.notes.toLowerCase().includes(query)) ||
          (card.assignedVehicle && card.assignedVehicle.toLowerCase().includes(query))
        );
      });
    }

    // Apply status filter
    if (statusFilter !== "all") {
      result = result.filter((card) => card.status === statusFilter);
    }

    // Apply assignment filter
    if (assignmentFilter === "assigned") {
      result = result.filter((card) => card.assignedVehicle !== null);
    } else if (assignmentFilter === "unassigned") {
      result = result.filter((card) => card.assignedVehicle === null);
    }

    // Sort by pool_number using natural sort (numeric order)
    result.sort((a, b) => naturalSort(a.pool_number, b.pool_number));

    return result;
  }, [cardsWithAssignedVehicle, searchQuery, statusFilter, assignmentFilter]);

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setAssignmentFilter("all");
  };

  const hasActiveFilters = searchQuery.trim() !== "" || statusFilter !== "all" || assignmentFilter !== "all";

  const toggleReveal = (cardId: string) => {
    if (!isAdmin) return;
    setRevealed((prev) => ({ ...prev, [cardId]: !prev[cardId] }));
  };

  const resetAddForm = () => {
    setNewCard({
      pool_number: "",
      card_type: "Petrol",
      pin: "",
      status: "active",
      notes: "",
    });
  };

  const addCard = async () => {
    if (!canManage) {
      toast({ title: "Not allowed", description: "Admin only.", variant: "destructive" });
      return;
    }

    if (!newCard.pool_number.trim()) {
      toast({
        title: "Missing pool number",
        description: "Please enter a pool number for the fuel card.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase.from("fuel_cards").insert({
        pool_number: newCard.pool_number.trim(),
        card_type: newCard.card_type,
        pin: newCard.pin.trim() || null,
        status: newCard.status,
        notes: newCard.notes.trim() || null,
      });

      if (error) throw error;

      toast({ title: "Fuel card added", description: `Card ${newCard.pool_number} created.` });
      setAddOpen(false);
      resetAddForm();
      fetchCards();
    } catch (e: any) {
      toast({
        title: "Error adding fuel card",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    }
  };

  const openEdit = (card: FuelCardRow) => {
    if (!canManage) return;
    setEditing(card);
    setEditValues({
      pool_number: card.pool_number,
      card_type: (card.card_type as FuelCardType) || "Petrol",
      pin: card.pin || "",
      status: (card.status as FuelCardStatus) || "active",
      notes: card.notes || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!canManage || !editing) return;

    if (!editValues.pool_number.trim()) {
      toast({
        title: "Missing pool number",
        description: "Please enter a pool number.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from("fuel_cards")
        .update({
          pool_number: editValues.pool_number.trim(),
          card_type: editValues.card_type,
          pin: editValues.pin.trim() || null,
          status: editValues.status,
          notes: editValues.notes.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", editing.id);

      if (error) throw error;

      toast({ title: "Fuel card updated", description: `Card ${editValues.pool_number} saved.` });
      setEditOpen(false);
      setEditing(null);
      fetchCards();
    } catch (e: any) {
      toast({
        title: "Error updating fuel card",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    }
  };

  const deactivateCard = async (card: FuelCardRow) => {
    if (!canManage) return;

    const newStatus = card.status === "active" ? "inactive" : "active";
    const action = newStatus === "inactive" ? "deactivated" : "reactivated";

    try {
      const { error } = await supabase
        .from("fuel_cards")
        .update({
          status: newStatus,
          updated_at: new Date().toISOString(),
        })
        .eq("id", card.id);

      if (error) throw error;

      toast({ title: `Fuel card ${action}`, description: `Card ${card.pool_number} is now ${newStatus}.` });
      fetchCards();
    } catch (e: any) {
      toast({
        title: "Error updating status",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-xl font-semibold">Fuel Cards</CardTitle>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchCards} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {canManage && (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add Card
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Filter Controls */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-[300px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search cards..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                onClick={() => setSearchQuery("")}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          <Select value={statusFilter} onValueChange={(val) => setStatusFilter(val as "all" | "active" | "inactive")}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>

          <Select value={assignmentFilter} onValueChange={(val) => setAssignmentFilter(val as "all" | "assigned" | "unassigned")}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Assignment" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cards</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
              <X className="h-4 w-4 mr-1" />
              Clear Filters
            </Button>
          )}

          {hasActiveFilters && (
            <span className="text-sm text-muted-foreground">
              Showing {filteredAndSortedCards.length} of {cardsWithAssignedVehicle.length} cards
            </span>
          )}
        </div>

        <div className="rounded-md border max-h-[600px] overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-card shadow-[0_1px_0_rgba(255,255,255,0.06)]">

              <TableRow>
                <TableHead>Pool Number</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>PIN</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assigned Vehicle</TableHead>
                <TableHead>Notes</TableHead>
                {canManage && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 7 : 6} className="text-center py-8 text-muted-foreground">
                    Loading fuel cards...
                  </TableCell>
                </TableRow>
              ) : filteredAndSortedCards.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 7 : 6} className="text-center py-8 text-muted-foreground">
                    {hasActiveFilters ? "No fuel cards match the current filters." : "No fuel cards found."}
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedCards.map((card) => (
                  <TableRow key={card.id}>
                    <TableCell className="font-medium">{card.pool_number}</TableCell>
                    <TableCell>{card.card_type || "-"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-mono">
                          {revealed[card.id] ? card.pin || "-" : maskPin(card.pin)}
                        </span>
                        {isAdmin && card.pin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => toggleReveal(card.id)}
                          >
                            {revealed[card.id] ? (
                              <EyeOff className="h-3.5 w-3.5" />
                            ) : (
                              <Eye className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(card.status)}>
                        {card.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {card.assignedVehicle ? (
                        <Badge variant="outline">{card.assignedVehicle}</Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">Unassigned</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                      {card.notes || "-"}
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => openEdit(card)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-8 w-8 p-0 ${card.status === "active" ? "text-destructive hover:text-destructive" : "text-green-600 hover:text-green-600"}`}
                            onClick={() => deactivateCard(card)}
                          >
                            <Ban className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      {/* Add Card Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Fuel Card</DialogTitle>
            <DialogDescription>
              Enter the details for the new fuel card.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="pool_number">Pool Number *</Label>
              <Input
                id="pool_number"
                value={newCard.pool_number}
                onChange={(e) => setNewCard({ ...newCard, pool_number: e.target.value })}
                placeholder="e.g., FC001"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="card_type">Card Type</Label>
              <Select
                value={newCard.card_type}
                onValueChange={(val) => setNewCard({ ...newCard, card_type: val as FuelCardType })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Petrol">Petrol</SelectItem>
                  <SelectItem value="Diesel">Diesel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="pin">PIN</Label>
              <Input
                id="pin"
                type="password"
                value={newCard.pin}
                onChange={(e) => setNewCard({ ...newCard, pin: e.target.value })}
                placeholder="Enter PIN"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={newCard.status}
                onValueChange={(val) => setNewCard({ ...newCard, status: val as FuelCardStatus })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={newCard.notes}
                onChange={(e) => setNewCard({ ...newCard, notes: e.target.value })}
                placeholder="Optional notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button onClick={addCard}>Add Card</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Card Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Fuel Card</DialogTitle>
            <DialogDescription>
              Update the fuel card details.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit_pool_number">Pool Number *</Label>
              <Input
                id="edit_pool_number"
                value={editValues.pool_number}
                onChange={(e) => setEditValues({ ...editValues, pool_number: e.target.value })}
                placeholder="e.g., FC001"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_card_type">Card Type</Label>
              <Select
                value={editValues.card_type}
                onValueChange={(val) => setEditValues({ ...editValues, card_type: val as FuelCardType })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Petrol">Petrol</SelectItem>
                  <SelectItem value="Diesel">Diesel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_pin">PIN</Label>
              <Input
                id="edit_pin"
                type="password"
                value={editValues.pin}
                onChange={(e) => setEditValues({ ...editValues, pin: e.target.value })}
                placeholder="Enter PIN"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_status">Status</Label>
              <Select
                value={editValues.status}
                onValueChange={(val) => setEditValues({ ...editValues, status: val as FuelCardStatus })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_notes">Notes</Label>
              <Textarea
                id="edit_notes"
                value={editValues.notes}
                onChange={(e) => setEditValues({ ...editValues, notes: e.target.value })}
                placeholder="Optional notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default FuelCardsTab;
